import { useState, useEffect } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import api from "@/lib/api";
import { Trash2, Ticket, Percent, IndianRupee, Clock, Calendar } from "lucide-react";

import { Badge } from "@/components/ui/badge";

export default function AdminCoupons() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState("all");
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form State
  const [code, setCode] = useState("");
  const [discountType, setDiscountType] = useState("percent");
  const [discountValue, setDiscountValue] = useState("");
  
  // New Fields
  const [maxDiscountAmount, setMaxDiscountAmount] = useState("");
  const [minOrderValue, setMinOrderValue] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [validStartTime, setValidStartTime] = useState("");
  const [validEndTime, setValidEndTime] = useState("");
  const [dailyLimit, setDailyLimit] = useState("");
  const [isOneTime, setIsOneTime] = useState(false);
  const [autoDeactivateDays, setAutoDeactivateDays] = useState("");

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      fetchCoupons(selectedProperty);
    }
  }, [selectedProperty]);

  const fetchProperties = async () => {
    try {
      const res = await api.get("/settings");
      setProperties(res.data);
      setSelectedProperty("all");
    } catch (error) {
      toast.error("Failed to load properties");
    }
  };

  const fetchCoupons = async (propertyId) => {
    setLoading(true);
    try {
      const res = await api.get(`/coupons?property_id=${propertyId}`);
      setCoupons(res.data);
    } catch (error) {
      toast.error("Failed to load coupons");
    } finally {
      setLoading(false);
    }
  };
  
  const generateRandomCode = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let randomCode = "";
    for (let i = 0; i < 8; i++) randomCode += chars.charAt(Math.floor(Math.random() * chars.length));
    setCode(randomCode);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!code || !discountValue) {
      toast.error("Please fill in code and discount value");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        property_id: selectedProperty,
        code: code.toUpperCase(),
        discount_type: discountType,
        discount_value: parseFloat(discountValue),
        max_discount_amount: maxDiscountAmount ? parseFloat(maxDiscountAmount) : null,
        min_order_value: minOrderValue ? parseFloat(minOrderValue) : null,
        valid_from: validFrom ? new Date(validFrom).toISOString() : null,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        valid_start_time: validStartTime || null,
        valid_end_time: validEndTime || null,
        daily_limit: dailyLimit ? parseInt(dailyLimit) : null,
        is_one_time: isOneTime,
        auto_deactivate_days: autoDeactivateDays ? parseInt(autoDeactivateDays) : null,
      };
      
      await api.post("/coupons", payload);
      
      toast.success("Coupon created successfully!");
      setCode("");
      setDiscountValue("");
      setMaxDiscountAmount("");
      setMinOrderValue("");
      setValidFrom("");
      setExpiresAt("");
      setValidStartTime("");
      setValidEndTime("");
      setDailyLimit("");
      setIsOneTime(false);
      setAutoDeactivateDays("");
      
      fetchCoupons(selectedProperty);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create coupon");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to deactivate this coupon?")) return;
    
    try {
      await api.delete(`/coupons/${id}`);
      toast.success("Coupon deactivated");
      fetchCoupons(selectedProperty);
    } catch (error) {
      toast.error("Failed to deactivate coupon");
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Discount Coupons</h1>
          <p className="text-gray-500">Create smart promo codes, vouchers, and meal-specific limits.</p>
        </div>

        <Card className="border-t-4 border-t-purple-500 shadow-sm">
          <CardHeader className="bg-gray-50 border-b pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Ticket className="w-5 h-5 text-purple-600" />
                Select Property / Global Scope
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="w-full md:w-1/3">
              <Label>Target Scope</Label>
              <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Property" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" className="font-bold text-purple-600">Global (All Properties)</SelectItem>
                  {properties.map(p => (
                    <SelectItem key={p.property_id} value={p.property_id}>
                      {p.property_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Ticket className="w-5 h-5" />
                  Create New Coupon
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreate} className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                        <Label>Coupon Code</Label>
                        <button type="button" onClick={generateRandomCode} className="text-xs text-purple-600 font-semibold hover:underline">
                            Randomize
                        </button>
                    </div>
                    <Input 
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      placeholder="e.g. SUMMER50"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select value={discountType} onValueChange={setDiscountType}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percent">Percentage %</SelectItem>
                          <SelectItem value="amount">Fixed Amount ₹</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Value</Label>
                      <Input 
                        type="number" 
                        step="0.01"
                        value={discountValue}
                        onChange={(e) => setDiscountValue(e.target.value)}
                        placeholder={discountType === 'percent' ? "e.g. 20" : "e.g. 500"}
                        required
                      />
                    </div>
                  </div>
                  
                  {discountType === 'percent' && (
                      <div className="space-y-2">
                        <Label className="text-xs text-gray-500">Max Discount Amount (₹)</Label>
                        <Input 
                          type="number" 
                          value={maxDiscountAmount}
                          onChange={(e) => setMaxDiscountAmount(e.target.value)}
                          placeholder="e.g. 500"
                        />
                      </div>
                  )}

                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Min Order Value (₹)</Label>
                    <Input 
                      type="number" 
                      value={minOrderValue}
                      onChange={(e) => setMinOrderValue(e.target.value)}
                      placeholder="e.g. 999"
                    />
                  </div>
                  
                  <div className="pt-4 border-t border-gray-100">
                      <h4 className="font-semibold text-sm mb-3">Scheduling & Duration</h4>
                      
                      <div className="grid grid-cols-2 gap-4 mb-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Live Date</Label>
                            <Input type="datetime-local" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} className="text-xs" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Expiry Date</Label>
                            <Input type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className="text-xs" />
                          </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1">
                            <Label className="text-xs">Valid Start Time</Label>
                            <Input type="time" value={validStartTime} onChange={(e) => setValidStartTime(e.target.value)} className="text-xs" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Valid End Time</Label>
                            <Input type="time" value={validEndTime} onChange={(e) => setValidEndTime(e.target.value)} className="text-xs" />
                          </div>
                      </div>
                  </div>
                  
                  <div className="pt-4 border-t border-gray-100">
                      <h4 className="font-semibold text-sm mb-3">Limits</h4>
                      <div className="space-y-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Daily Global Uses (Max)</Label>
                            <Input type="number" value={dailyLimit} onChange={(e) => setDailyLimit(e.target.value)} placeholder="e.g. 50 uses per day" className="text-xs" />
                          </div>
                          
                          <label className="flex items-center gap-2 mt-2 cursor-pointer border p-2 rounded-lg bg-gray-50">
                              <input type="checkbox" checked={isOneTime} onChange={(e) => setIsOneTime(e.target.checked)} className="w-4 h-4 text-purple-600 rounded" />
                              <span className="text-sm font-medium">One-Time Use Only</span>
                          </label>
                      </div>
                  </div>

                  <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700" disabled={saving}>
                    {saving ? "Creating..." : "Create Smart Coupon"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-2 space-y-4">
            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading coupons...</div>
            ) : coupons.length === 0 ? (
              <Card className="bg-gray-50 border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <Ticket className="w-12 h-12 mb-4 text-gray-300" />
                  <p>No active coupons found for this scope.</p>
                </CardContent>
              </Card>
            ) : (
              coupons.map((coupon) => (
                <Card key={coupon.id} className={!coupon.is_active ? "opacity-60" : ""}>
                  <CardContent className="p-4 flex flex-col md:flex-row justify-between gap-4 md:items-center">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-lg">{coupon.code}</h3>
                        {!coupon.is_active && (
                          <Badge variant="secondary" className="bg-red-100 text-red-700">Deactivated</Badge>
                        )}
                        {coupon.is_one_time && (
                          <Badge variant="outline" className="text-purple-600 border-purple-200 bg-purple-50">One-Time Use</Badge>
                        )}
                      </div>
                      <p className="text-gray-600 font-medium">
                        {coupon.discount_type === 'percent' 
                          ? `${coupon.discount_value}% OFF` 
                          : `₹${coupon.discount_value} OFF`}
                          
                        {coupon.max_discount_amount && <span className="text-xs text-gray-500 ml-1">(Max ₹{coupon.max_discount_amount})</span>}
                      </p>
                      
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                          {coupon.min_order_value && <span>Min Order: ₹{coupon.min_order_value}</span>}
                          {coupon.valid_start_time && <span>Hours: {coupon.valid_start_time} - {coupon.valid_end_time}</span>}
                          {coupon.expires_at && <span>Expires: {new Date(coupon.expires_at).toLocaleDateString()}</span>}
                          <span>Total Uses: {coupon.used_count || 0}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(coupon.id)}
                        disabled={!coupon.is_active}
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
